import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { MdDelete, MdAdd, MdSave, MdHistory } from 'react-icons/md';
import Select from 'react-select';
import { api } from '../../utils/services/api';
import { SERVICE } from '../../utils/constants/services';
import Swal from 'sweetalert2';

export default function BannedListModal({ show, setShow, selectedClients, onSuccess }) {
    const [reasons, setReasons] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNewReason, setShowNewReason] = useState(false);
    const [newReasonDesc, setNewReasonDesc] = useState('');
    const [newReasonMessage, setNewReasonMessage] = useState('');
    const [loadingReasons, setLoadingReasons] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedClientForHistory, setSelectedClientForHistory] = useState(null);

    useEffect(() => {
        if (show) {
            loadReasons();
            setMessage('');
            setSelectedReason(null);
        }
    }, [show]);

    const loadReasons = async () => {
        setLoadingReasons(true);
        try {
            const response = await api(SERVICE.getReasonsBannedList);
            if (response.data.data) {
                const reasonsArray = response.data.data.map(item => ({
                    value: item.id,
                    label: item.description,
                    descriptionMessage: item.descriptionMessage
                }));
                setReasons(reasonsArray);
            }
        } catch (error) {
            console.error('Error al cargar razones:', error);
            Swal.fire('Error', 'No se pudieron cargar las razones', 'error');
        } finally {
            setLoadingReasons(false);
        }
    };

    const handleAddReason = async () => {
        if (!newReasonDesc.trim()) {
            Swal.fire('Atención', 'Ingrese una descripción para la razón', 'warning');
            return;
        }

        try {
            const payload = {
                Description: newReasonDesc.trim(),
                DescriptionMessage: newReasonMessage.trim() || newReasonDesc.trim()
            };

            await api(SERVICE.setReasonsBannedList, payload, 'POST');
            Swal.fire('Éxito', 'Razón agregada correctamente', 'success');
            
            setNewReasonDesc('');
            setNewReasonMessage('');
            setShowNewReason(false);
            await loadReasons();
        } catch (error) {
            console.error('Error al agregar razón:', error);
            Swal.fire('Error', 'No se pudo agregar la razón', 'error');
        }
    };

    const handleDeleteReason = async (reasonId) => {
        const result = await Swal.fire({
            title: '¿Eliminar esta razón?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await api(SERVICE.deleteReasonsBannedList + `?Id=${reasonId}`, {}, 'DELETE');
                Swal.fire('Eliminado', 'La razón ha sido eliminada', 'success');
                
                if (selectedReason && selectedReason.value === reasonId) {
                    setSelectedReason(null);
                }
                
                await loadReasons();
            } catch (error) {
                console.error('Error al eliminar razón:', error);
                Swal.fire('Error', 'No se pudo eliminar la razón', 'error');
            }
        }
    };

    const handleSubmit = async () => {
        if (!selectedReason) {
            Swal.fire('Atención', 'Seleccione una razón', 'warning');
            return;
        }

        setIsSubmitting(true);

        try {
            const currentDate = new Date().toISOString().split('T')[0];

            for (const client of selectedClients) {
                const payload = {
                    ClientId: client.clientId,
                    Banned: true,
                    ReasonsBannedListId: selectedReason.value,
                    Date: currentDate,
                    Message: message.trim()
                };

                await api(SERVICE.setBannedListClient, payload, 'POST');
            }

            Swal.fire('Éxito', `${selectedClients.length} cliente(s) agregado(s) a la lista negra`, 'success');
            setShow(false);
            
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error al agregar a lista negra:', error);
            Swal.fire('Error', 'No se pudo completar la operación', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setShow(false);
            setShowNewReason(false);
            setNewReasonDesc('');
            setNewReasonMessage('');
            setShowHistory(false);
            setHistoryData([]);
            setSelectedClientForHistory(null);
        }
    };

    const loadHistory = async (clientId) => {
        setLoadingHistory(true);
        try {
            const response = await api(SERVICE.getBannedListHistory + `?clientId=${clientId}`);
            if (response.data.data) {
                setHistoryData(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar historial:', error);
            Swal.fire('Error', 'No se pudo cargar el historial', 'error');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleShowHistory = (client) => {
        setSelectedClientForHistory(client);
        loadHistory(client.clientId);
        setShowHistory(true);
    };

    const handleCloseHistory = () => {
        setShowHistory(false);
        setHistoryData([]);
        setSelectedClientForHistory(null);
    };

    return (
        <>
            <Modal 
                show={show && !showHistory} 
                onHide={handleClose} 
                size="lg" 
                backdrop="static" 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Agregar a Lista Negra</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Clientes seleccionados */}
                    <div className="mb-4">
                        <h6 className="fw-bold">Clientes seleccionados ({selectedClients.length}):</h6>
                        <div style={{ maxHeight: '150px', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px' }}>
                            {selectedClients.map((client, index) => (
                                <div key={client.clientId} style={{ padding: '5px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>
                                        {index + 1}. {client.clientName}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={() => handleShowHistory(client)}
                                        title="Ver historial"
                                    >
                                        <MdHistory />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                {/* Sección de razones */}
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-bold mb-0">Razón <span style={{ color: 'red' }}>*</span></label>
                        <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => setShowNewReason(!showNewReason)}
                            disabled={isSubmitting}
                        >
                            <MdAdd /> {showNewReason ? 'Cancelar' : 'Nueva Razón'}
                        </button>
                    </div>

                    {/* Formulario para nueva razón */}
                    {showNewReason && (
                        <div className="card mb-3" style={{ backgroundColor: '#f0f8ff', border: '1px solid #0d6efd' }}>
                            <div className="card-body">
                                <div className="mb-2">
                                    <label className="form-label">Descripción <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: Documentación falsa"
                                        value={newReasonDesc}
                                        onChange={(e) => setNewReasonDesc(e.target.value)}
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Mensaje descriptivo</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        placeholder="Mensaje adicional (opcional)"
                                        value={newReasonMessage}
                                        onChange={(e) => setNewReasonMessage(e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleAddReason}
                                >
                                    <MdSave /> Guardar Razón
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Selector de razón con botón de eliminar en el mismo renglón */}
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ flex: 1 }}>
                            <Select
                                options={reasons}
                                value={selectedReason}
                                onChange={setSelectedReason}
                                placeholder="Seleccione una razón..."
                                isDisabled={isSubmitting || loadingReasons}
                                isLoading={loadingReasons}
                                isClearable
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => selectedReason && handleDeleteReason(selectedReason.value)}
                            disabled={isSubmitting || !selectedReason}
                            title="Eliminar razón seleccionada"
                        >
                            <MdDelete />
                        </button>
                    </div>
                </div>

                {/* Mensaje adicional */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Mensaje adicional (opcional)</label>
                    <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Ingrese información adicional sobre el motivo..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isSubmitting}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button variant="danger" onClick={handleSubmit} disabled={isSubmitting || !selectedReason}>
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Agregando...
                        </>
                    ) : (
                        <>Agregar a Lista Negra</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Modal de Historial */}
        <Modal 
            show={showHistory} 
            onHide={handleCloseHistory} 
            size="lg" 
            centered
            backdrop="static"
            style={{ zIndex: 1060 }}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Historial de Lista Negra - {selectedClientForHistory?.clientName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadingHistory ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                ) : historyData.length === 0 ? (
                    <div className="alert alert-info">
                        No hay historial disponible para este cliente
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Fecha</th>
                                    <th>Acción</th>
                                    <th>Razón</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>{new Date(item.date).toLocaleDateString('es-MX')}</td>
                                        <td>
                                            {item.banned ? (
                                                <span className="badge bg-danger">Agregado a lista negra</span>
                                            ) : (
                                                <span className="badge bg-success">Removido de lista negra</span>
                                            )}
                                        </td>
                                        <td>{item.description || '-'}</td>
                                        <td>{item.userName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseHistory}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    );
}
